import ListTemplate from "components/ListTemplate";
import moment from "moment";
import React from "react";
import { formatShortDate } from "utilities/momentutils";
import { ChangeOwnerModal, ChangeStatusModal } from "components/Tables/MySelection/Modals";
import DocumentLifeCyclePill from "components/Document/DocumentLifeCyclePill";
import LinkedShowsList from "components/LinkedShows/LinkedShowsList";
import { Status } from "components";
import ChecklistStatus from "components/ChecklistStatus/ChecklistStatus";
import DocumentESignStatus from "components/DocumentSignStatus/DocumentESignStatus";
import { DotLabel } from "components/Dot";
import Filters from "./Filters";
import { type AxiosPromise } from "axios";
import { getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { mapFilterValuesToInstruction, mapResponseToFilterValues } from "./helpers";
import { useLoaderData } from "react-router";
import { getDocumentRoutes } from "utilities/documentUtils";

interface Props {
  viewModel: server.viewmodel.DocumentListFrontModel
  showCosts?: boolean
  permission: server.dto.SectionWithAmountPermission
  hasStatuses: boolean;
  filterPreset: {
    cacheKey: server.dto.FilterSets
    listApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedDocumentInstruction>[]>>
    mutateApi: (instruction: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedDocumentInstruction>) => AxiosPromise<any>
  }
}

const DetailUrl = ({ document }: { document: server.dto.DocumentForList }) => {
  const routes = getDocumentRoutes(document.documentType, document.documentId);
  return routes.detail;
};

const ESignUrl = ({ document }: { document: server.dto.DocumentForList }) => {
  const routes = getDocumentRoutes(document.documentType, document.documentId);
  return routes.esign;
}

const DocumentIcons = ({ document }: { document: server.dto.DocumentForList }) => {
  return <div className="flex-column">
    {document.locked === "locked" && <div className="padding-right">
      <i className="icon-lock" />
    </div>}
    {document.isV2 && <div className="padding-right">
      <i className="icon-rocket" />
    </div>}
  </div>;
};

const DocumentNumber = ({ document }: { document: server.dto.DocumentForList }) => {
  const href = document.isV1
    ? DetailUrl({ document })
    : ESignUrl({ document });

  return <div className="flex">
    <div>
      <a href={href}>{document.documentNumber}</a>
      <div><DocumentLifeCyclePill value={document.lifeCycle.value} overDue={document.overDue} /></div>
    </div>
  </div>;
};

const DocumentStatus = ({ document }: { document: server.dto.DocumentForList }) => {
  if (document.isV1) {
    if (document.lifeCycle.value === "discarded") {
      return <DotLabel dotColor="s1_lightergray">{document.status.title}</DotLabel>;
    }
    return <Status status={document.status} />;
  } else if (document.lifeCycle.value === "review") {
    return <>
      {document.checklist.notSet > 0 && <ChecklistStatus count={document.checklist.notSet} type="unset" />}
      {document.checklist.approved > 0 && <ChecklistStatus count={document.checklist.approved} type="approved" />}
      {document.checklist.declined > 0 && <ChecklistStatus count={document.checklist.declined} type="declined" />}
    </>;
  } else if (document.lifeCycle.value === "pending" || document.lifeCycle.value === "completed") {
    return <DocumentESignStatus sender={document.senderProgress} signer={document.signerProgress} />;
  }

  return null;
};

const DueDate = ({ document }: { document: server.dto.DocumentForList }) => {
  return <span className={document.overDue ? "red" : ""}>{formatShortDate(moment(document.dueDate))}</span>;
};

const SalesCosts = ({ money }: { money: server.dto.MoneyWithExchangeRate }) => {
  return <>
    {money.formatted}
    {money.baseCurrency !== money.currency &&
      <div className="text-sublabel">{money.formattedBaseCurrencyAmount}</div>}
  </>;
};

const DocumentListApp = (props: Props) => {
  return <>
    <ListTemplate
      title={props.viewModel.pageTitle}
      filterSet={props.viewModel.filterSet}
      defaultFilters={props.viewModel.defaultFilters}
      initialInstruction={props.viewModel.initialInstruction}
      fetchFunction={getDocumentCoreApiExceptInvoice(props.viewModel.mainArea).Paginated}
      getId={item => item.documentId}
      layout={{
        type: "table",
        columns: [{
          key: "icons",
          header: "",
          render: item => <DocumentIcons document={item} />,
          size: "25px"
        }, {
          key: "number",
          header: RESX.GeneralLabels.Number,
          render: item => <DocumentNumber document={item} />,
          size: "fit-content(140px)"
        }, {
          key: "status",
          header: "",
          render: item => <DocumentStatus document={item} />,
          size: "0.6fr"
        }, {
          key: "addressee",
          header: RESX.Document.Addressee,
          render: item => item.addressee.name,
          size: "1.5fr"
        }, {
          key: "send",
          header: RESX.DateTime.Send,
          render: item => `${formatShortDate(moment(item.sendDate))}`,
          size: "auto"
        }, {
          key: "due",
          header: RESX.DateTime.Due,
          render: item => <DueDate document={item} />,
          size: "auto"
        }, {
          key: "costs",
          header: RESX.Financial.Costs,
          render: item => <SalesCosts money={item.costs} />,
          hidden: !props.permission.canReadCosts || !props.showCosts,
          className: "s1_cell_financialamount",
          size: "auto"
        }, {
          key: "sales",
          header: RESX.Financial.Sales,
          render: item => <SalesCosts money={item.sales} />,
          hidden: !props.permission.canReadSales,
          className: "s1_cell_financialamount",
          size: "auto"
        }, {
          key: "linkedTo",
          header: RESX.GeneralLabels.LinkedTo,
          render: item => <LinkedShowsList shows={item.linkedShows} />,
          size: "2.2fr"
        }]
      }}
      filters={<Filters documentType={props.viewModel.documentType} hasStatuses={props.hasStatuses} statuses={props.viewModel.statuses} />}
      functions={{
        mapFilterValuesToInstruction,
        mapResponseToFilterValues: mapResponseToFilterValues(props.viewModel.documentType)
      }}
      onEdit={document => {
        window.location.href = document.isV1 ? DetailUrl({ document }) : ESignUrl({ document });
      }}
      options={{
        filterPreset: props.filterPreset,
        exportOptions: {
          canExportList: props.permission.canExportList,
          exportOptionsApi: getDocumentCoreApiExceptInvoice(props.viewModel.mainArea).ExportOptionsForList // This is now either Core.Quote | Core
        },
        mySelection: {
          isSelected: item => item.isSelected,
          mainArea: props.viewModel.mainArea,
          batchActions: [props.hasStatuses && {
            label: RESX.Status.ChangeStatus,
            modal: ChangeStatusModal,
            props: { statuses: props.viewModel.statuses }
          }, {
            label: RESX.Planning.ChangeOwner,
            modal: ChangeOwnerModal
          }].filter(Boolean)
        }
      }}
    />
  </>;
};

export const QuoteListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.DocumentListFrontModel>();

  const onlyHasV2 = site.currentEnvironment.quote.hasV2 && !site.currentEnvironment.quote.hasV1;

  return <DocumentListApp
    viewModel={viewModel}
    showCosts={false}
    permission={site.permissions.quote}
    hasStatuses={viewModel.statuses.length > 0 && !onlyHasV2}
    filterPreset={{
      cacheKey: "quote_List",
      listApi: window.CoreApi.FilterPreset.ListFilterPresetsForQuote,
      mutateApi: window.CoreApi.FilterPreset.CreateFilterPresetForDocument
    }}
  />
}

export const PurchaseAgreementListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.DocumentListFrontModel>();

  const onlyHasV2 = site.currentEnvironment.purchaseAgreement.hasV2 && !site.currentEnvironment.purchaseAgreement.hasV1;

  return <DocumentListApp
    viewModel={viewModel}
    showCosts={true}
    permission={site.permissions.purchaseAgreement}
    hasStatuses={viewModel.statuses.length > 0 && !onlyHasV2}
    filterPreset={{
      cacheKey: "purchaseAgreement_List",
      listApi: window.CoreApi.FilterPreset.ListFilterPresetsForPurchaseAgreement,
      mutateApi: window.CoreApi.FilterPreset.CreateFilterPresetForDocument
    }}
  />
}

export const ContractListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.DocumentListFrontModel>();

  const onlyHasV2 = site.currentEnvironment.contract.hasV2 && !site.currentEnvironment.contract.hasV1;

  return <DocumentListApp
    viewModel={viewModel}
    showCosts={false}
    permission={site.permissions.contract}
    hasStatuses={viewModel.statuses.length > 0 && !onlyHasV2}
    filterPreset={{
      cacheKey: "contract_List",
      listApi: window.CoreApi.FilterPreset.ListFilterPresetsForContract,
      mutateApi: window.CoreApi.FilterPreset.CreateFilterPresetForDocument
    }}
  />
}